import { Autocomplete, Divider, FormControlLabel, Grid, IconButton, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import RefreshIcon from '@mui/icons-material/Refresh';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';

import popupStyles from '../../../assets/styles/componentStyles/globalPopupStyles.module.scss';
import styles from '../../../assets/styles/componentStyles/importChatbotPopup.module.scss';
import globalStyles from '../../../assets/styles/global.module.scss';
import GlobalTooltip from '../../utility/tooltip/globalTooltip';

import Required from '../../utility/required';
import GlobalButton from '../../utility/globalButton/button';
import agent from '../../../services/api';
import { WorkflowContext, WorkflowContextPayload } from '../../../context/workflowContext';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { chatbotIntegratedChannelDeploymentObj, chatbotVersionInfoInterface, getChatbotListNewReponseModel, staticWorkflowChatbotDetails } from '../../../models/axiosModel';
import { isWhatsAppChannel } from '../../../pages/ruleBasedInbox/channelConstants';
import { useGenericStore } from '../../../store/genericStore';
import { createDeploymentPayload, createImportRuleBasedChatbotPayload, ImportChatbotVersion, importCustomVariables } from './importRuleBasedChatbotPayload';

const VERSION_OPTIONS: ImportChatbotVersion[] = ["Draft", "Published"];

interface ImportRuleBasedChatbotPopupProps {
    onClose: () => void,
}

const ImportRuleBasedChatbotPopup = (props: ImportRuleBasedChatbotPopupProps) => {

    const { onClose } = props;

    const navigate = useNavigate();

    const { showSuccess, showError } = useGenericStore(useShallow((state) => ({ 
        showSuccess: state.showSuccess, 
        showError: state.showError 
    })));

    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { currentActiveStaticWorkflow, existingWorkFlowList, getExistingWorkflowList, updateChatbotDeploymentDetails, setCurrentActiveStaticWorkflow } = useContext(WorkflowContext) as WorkflowContextPayload;

    const [selectedChatbot, setSelectedChatbot] = useState<getChatbotListNewReponseModel | null>(null);
    const [version, setVersion] = useState<ImportChatbotVersion>("Draft");
    const [sourceChatbotDetail, setSourceChatbotDetail] = useState<staticWorkflowChatbotDetails | null>(null); // Details of the selected chatbot

    const versionOptions = VERSION_OPTIONS.filter((option) => sourceChatbotDetail?.chatbot_version_info?.some((item: chatbotVersionInfoInterface) => item?.chatbot_version_status_name?.toLowerCase() === option.toLowerCase()));
    const activeVersion = versionOptions.includes(version) ? version : versionOptions[0];

    // WABA and phone number selected for the current chatbot (empty while the selection is pending)
    const currentDeployment = currentActiveStaticWorkflow?.chatbot_integrated_channel_deployment?.integrated_channel_data?.[0] as chatbotIntegratedChannelDeploymentObj | undefined;
    const currentMetaIntegrationWhatsAppId = currentDeployment?.meta_integration_whatsapp_id;
    const currentIntegratedChannelId = currentDeployment?.meta_integration_whatsapp_phone_number_data?.[0]?.integrated_channel_id;
    const isDeploymentPending = !currentMetaIntegrationWhatsAppId || !currentIntegratedChannelId;

    // No WABA selected: all WhatsApp chatbots. WABA selected: chatbots of that WABA. WABA and phone number selected: chatbots of that WABA and phone number.
    // The current chatbot is excluded. A chatbot's WABA comes from its phone number, or from `meta_integration_whatsapp_data` when only the WABA is connected.
    const chatbotOptions = useMemo(() => {
        return (existingWorkFlowList ?? []).filter((chatbot) => {
            
            const phoneNumber = chatbot.meta_integration_whatsapp_phone_number_data?.[0];
            const chatbotMetaIntegrationWhatsAppId = phoneNumber?.meta_integration_whatsapp_id ?? chatbot.meta_integration_whatsapp_data?.meta_integration_whatsapp_id;
            
            return chatbot.chatbot_id !== currentActiveStaticWorkflow?.chatbot_id
                && isWhatsAppChannel(chatbot.chatbot_channel?.channel_name)
                && (!currentMetaIntegrationWhatsAppId || String(chatbotMetaIntegrationWhatsAppId) === String(currentMetaIntegrationWhatsAppId))
                && (!currentIntegratedChannelId || String(phoneNumber?.integrated_channel_id) === String(currentIntegratedChannelId));
        });
    }, [existingWorkFlowList, currentActiveStaticWorkflow?.chatbot_id, currentMetaIntegrationWhatsAppId, currentIntegratedChannelId]);


    // Fetch the selected chatbot's details (versions, steps)
    const onChangeOfChatbot = async (chatbot: getChatbotListNewReponseModel | null) => {
        setSelectedChatbot(chatbot);
        setVersion("Draft");
        setSourceChatbotDetail(null);

        if (!chatbot) return;

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));

        const res = await agent.getChatbotConfig.get(chatbot.chatbot_id);
        if (res.status) {
            setSourceChatbotDetail(res.data?.[0] ?? null);
        } else {
            handleError(res);
            setSelectedChatbot(null);
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    // Handle Import Chatbot: complete the WABA / phone number (if pending) -> create variables -> build payload -> import -> redirect to list
    const handleImport = async () => {
        const destChatbotId = currentActiveStaticWorkflow?.chatbot_id;
        if (!selectedChatbot || !sourceChatbotDetail || !activeVersion || !currentActiveStaticWorkflow || !destChatbotId) return;

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        try {
            // Step 1: If the WABA or phone number selection is pending, complete it from the source chatbot
            if (isDeploymentPending) {
                const deploymentPayload = createDeploymentPayload(currentActiveStaticWorkflow, sourceChatbotDetail);
                if (deploymentPayload) {
                    const deploymentRes = await updateChatbotDeploymentDetails(deploymentPayload);
                    if (!deploymentRes.status) {
                        handleError(deploymentRes);
                        return;
                    }

                    // Keep the chatbot's deployment in sync, so the chatbot list is filtered accordingly if the import stops later
                    setCurrentActiveStaticWorkflow((prev) => prev ? {
                        ...prev,
                        chatbot_integrated_channel_deployment: deploymentRes.data?.[0]?.chatbot_integrated_channel_deployment ?? prev.chatbot_integrated_channel_deployment,
                    } : prev);
                }
            }

            // Step 2: Create Variables
            const { error, variableIdMap } = await importCustomVariables(selectedChatbot.chatbot_id, destChatbotId);
            if (error) {
                return handleError(error);
            }

            // Step 3: Create Payload - Nodes, edges
            const { payload, errors } = createImportRuleBasedChatbotPayload({
                sourceChatbotDetail,
                version: activeVersion,
                destChatbotId,
                variableIdMap
            });
            if (!payload) {
                return showError(errors[0]);
            }

            // Step 4: Import Chatbot
            agent.importRuleBasedChatbot.put(payload, destChatbotId)
                .then((res: any) => res.status ? showSuccess("Importing chatbot, this may take a while.") : handleError(res))
                .catch(handleError);

            // Step 5: Redirect to the chatbot list page
            onClose();
            navigate('/workflow');
        } catch (err) {
            handleError(err);
        } finally {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    }

    // Refresh Chatbots
    const refreshChatbots = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        await getExistingWorkflowList();
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    // EFFECTS

    // Fetch the chatbots
    useEffect(() => {
        if (!existingWorkFlowList) {
            refreshChatbots();
        }
    }, [existingWorkFlowList]);

    // Clear the selection if the selected chatbot is no longer in the list
    useEffect(() => {
        if (selectedChatbot && !chatbotOptions.some((chatbot) => chatbot.chatbot_id === selectedChatbot.chatbot_id)) {
            onChangeOfChatbot(null);
        }
    }, [chatbotOptions]);

    return (
        <Grid container direction="column" justifyContent="center" className={globalStyles.boxSizingBorderBox}>
            
            {/* Title */}
            <Grid item textAlign="center">
                <Typography className={popupStyles.popupTitleTypography}>
                    Import Chatbot
                </Typography>
            </Grid>

            <Divider className={popupStyles.popupTitleDivider} />

            <Grid item>
                <Grid container className={popupStyles.popupBodyGridContainer} gap="20px">
                    
                    {/* Chatbot */}
                    <Grid item xs={12}>
                        <Grid container gap="12px">
                            
                            <Grid item xs={12}>
                                <Grid container className={styles.importFieldLabelRow}>

                                    <Grid item className={styles.importChatbotRow}>
                                        <Typography className={styles.importFieldLabel}>
                                            Select Chatbot to Import From <Required />
                                        </Typography>

                                        {selectedChatbot && 
                                            <GlobalTooltip 
                                                title="View chatbot in detail" 
                                                placement="top" 
                                                arrow
                                            >
                                                <IconButton 
                                                    className={styles.iconContainer} 
                                                    onClick={() => window.open(`/workflow/staticWorkflowDetails?workflow_id=${selectedChatbot?.chatbot_id}`, "_blank")}
                                                >
                                                    <LaunchOutlinedIcon className={styles.icon} />
                                                </IconButton>
                                            </GlobalTooltip>
                                        }
                                    </Grid>

                                    <GlobalTooltip 
                                        title="Fetch latest chatbots" 
                                        placement="top" 
                                        arrow
                                    >
                                        <IconButton 
                                            className={styles.iconContainer} 
                                            onClick={refreshChatbots}
                                        >
                                            <RefreshIcon className={styles.icon} />
                                        </IconButton>
                                    </GlobalTooltip>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    className={`${globalStyles.autocompleteParaphrasing} ${styles.importAutocomplete}`}
                                    options={chatbotOptions}
                                    value={selectedChatbot}
                                    getOptionLabel={(option) => option?.chatbot_name ?? ""}
                                    isOptionEqualToValue={(option, value) => option.chatbot_id === value.chatbot_id}
                                    onChange={(_, value) => onChangeOfChatbot(value)}
                                    ListboxProps={{ className: styles.importListbox }}
                                    noOptionsText="No WhatsApp chatbots found."
                                    renderInput={(params) => <TextField {...params} placeholder="Choose a chatbot" />}
                                />
                            </Grid>

                        </Grid>
                    </Grid>

                    {/* Version */}
                    {selectedChatbot &&
                        <Grid item xs={12}>
                            <Grid container gap="12px">

                                <Grid item xs={12}>
                                    <Typography className={styles.importFieldLabel}>
                                        Select Version <Required />
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <RadioGroup
                                        row
                                        className={styles.importRadioGroup}
                                        value={activeVersion ?? ""}
                                        onChange={(e) => setVersion(e.target.value as ImportChatbotVersion)}
                                    >
                                        {versionOptions.map((option) => (
                                            <FormControlLabel
                                                className={styles.importRadioLabel}
                                                key={option}
                                                value={option}
                                                label={option}
                                                control={<Radio className={styles.importRadio} size="small" disableRipple />}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Grid>
                                
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>

            {/* Actions */}
            <Grid item>
                <Grid container className={popupStyles.popupButtonContainer}>
                    <Grid item>
                        <GlobalButton 
                            buttonText="Cancel" 
                            className='borderedButtonStyle' 
                            onClick={onClose} 
                        />
                    </Grid>

                    <Grid item>
                        <GlobalButton
                            buttonText="Import"
                            className='primaryButtonStyle'
                            disabled={!sourceChatbotDetail || !activeVersion}
                            onClick={handleImport}
                        />
                    </Grid>
                </Grid>
            </Grid>

        </Grid>
    )
}

export default ImportRuleBasedChatbotPopup
